<div
  class="rail-card"
  [ngClass]="policyClass"
  role="button"
  [attr.tabindex]="isBlocked ? -1 : 0"
  [attr.aria-disabled]="isBlocked ? true : null"
  [attr.aria-expanded]="isExpanded"
  [attr.aria-label]="fareOptionsLabel + ' ' + card.from + ' to ' + card.to"
  (click)="toggleExpand()"
  (keydown.enter)="onCardKey($event)"
  (keydown.space)="onCardKey($event)"
>
  @if (policyLabel) {
    <span class="visuallyhidden">{{ policyLabel }}</span>
  }
  <div class="rail-card__content" [class.rail-card__content--eu]="isEuRail">
    <div class="rail-card__body" [class.rail-card__body--with-badge]="isEuRail && card.operator">
      @if (isEuRail && card.operator) {
        <div class="rail-card__badge-col">
          @if (card.operatorCode) {
            <img
              class="rail-card__operator-logo"
              alt="{{ card.operator }} logo"
              src="https://images.sabscorp.com/images/enterprise/lightUK/assets/images/{{
                card.operatorCode
              }}.svg"
            />
          } @else {
            <span class="rail-card__operator-badge" [ngClass]="operatorBadgeClass">{{
              card.operator
            }}</span>
          }
        </div>
      }

      <div class="rail-card__main">
        <span class="rail-card__date">{{ card.departDate }}</span>
        <div class="rail-card__journey">
          <div class="rail-card__time-group">
            <span class="rail-card__time">{{ card.departTime }}</span>
            <span class="rail-card__station">{{ card.from }}</span>
          </div>
          <div class="rail-card__middle">
            @if (card.duration) {
              <span class="rail-card__duration">{{ card.duration }}</span>
            }
            <span class="rail-card__changes">{{ changesLabel }}</span>
          </div>
          <div class="rail-card__time-group rail-card__time-group--right">
            <span class="rail-card__time">{{ card.arriveTime }}</span>
            <span class="rail-card__station">{{ card.to }}</span>
          </div>
        </div>
      </div>

      <div class="rail-card__price-col">
        @if (!isEuRail && card.operator) {
          <span class="rail-card__operator">{{ card.operator }}</span>
        }
        @if (isEuRail) {
          <span class="rail-card__price-label" i18n="@@chatEuRailFrom">From</span>
        } @else {
          <span class="rail-card__price-label" i18n="@@chatRailFromPrice">Singles from</span>
        }
        <span class="rail-card__price">{{ formattedPrice }}</span>
        @if (!isEuRail && cheapestClass) {
          <span class="rail-card__class-pill">{{ cheapestClass }}</span>
        }
        @if (isHeadlineSplit) {
          <app-split-ticket-icon
            class="rail-card__split-icon rail-card__split-icon--headline"
            [savings]="headlineSavings"
            [currency]="headlineCurrency"
          ></app-split-ticket-icon>
        }
        <!-- Policy indicator: same component/tooltip as the results page,
             placed under the headline fare per the travel-policy spec. -->
        <app-warning-tags
          [ticket]="headlineWarningTicket"
          [displayCheck]="false"
          (click)="$event.stopPropagation()"
        ></app-warning-tags>
      </div>
    </div>
  </div>

  <!-- Fare breakdown panel, shown on expand -->
  @if (isExpanded && card.fares; as fares) {
    <div
      class="rail-card__fares"
      [class.rail-card__fares--eu]="isEuRail"
      (click)="$event.stopPropagation()"
    >
      @if (!isEuRail) {
        <div class="rail-card__fares-title" i18n="@@chatRailAvailableFares">Available fares</div>
      }

      @if (isEuRail) {
        <!-- EU rail: flat list of fare class options -->
        @if (fares.singleTickets?.length) {
          <ul class="rail-card__ticket-list">
            @for (ticket of fares.singleTickets; track $index) {
              <li class="rail-card__ticket">
                <span class="rail-card__ticket-type">{{ ticket.type }}</span>
                <span class="rail-card__ticket-price">{{
                  formatCurrency(ticket.price, ticket.currency)
                }}</span>
              </li>
            }
          </ul>
        }
      } @else {
        <!-- UK rail: single/return sections -->
        <div class="rail-card__fares-grid">
          @if (fares.singlePrice != null) {
            <button
              type="button"
              class="rail-card__fare-item"
              [class.rail-card__fare-item--active]="expandedSection === 'single'"
              (click)="toggleSection('single', $event)"
            >
              <span class="rail-card__fare-label">{{ singleFareLabel }}</span>
              <span class="rail-card__fare-price">{{
                formatCurrency(fares.singlePrice, fares.currency)
              }}</span>
            </button>
          }
          @if (!isReturn && fares.returnPrice != null) {
            <button
              type="button"
              class="rail-card__fare-item"
              [class.rail-card__fare-item--active]="expandedSection === 'return'"
              (click)="toggleSection('return', $event)"
            >
              <span class="rail-card__fare-label">{{ returnFareLabel }}</span>
              <span class="rail-card__fare-price">{{
                formatCurrency(fares.returnPrice, fares.currency)
              }}</span>
            </button>
          }
        </div>

        <!-- Full ticket list, shown inline when a fare section is selected -->
        @if (expandedSection === 'single' && fares.singleTickets?.length) {
          <div class="rail-card__tickets">
            <div class="rail-card__tickets-title" i18n="@@chatRailTicketTypes">Ticket Types</div>
            <ul class="rail-card__ticket-list">
              @for (ticket of fares.singleTickets; track $index) {
                <li class="rail-card__ticket">
                  <div class="rail-card__ticket-info">
                    <span class="rail-card__ticket-type">{{ ticket.type }}</span>
                    @if (ticket.isSplit) {
                      <app-split-ticket-icon
                        class="rail-card__split-icon"
                        [savings]="ticket.savings || ''"
                        [currency]="ticket.currency"
                      ></app-split-ticket-icon>
                    }
                    <app-warning-tags
                      [ticket]="warningTicketFor(ticket)"
                      [displayCheck]="false"
                      (click)="$event.stopPropagation()"
                    ></app-warning-tags>
                  </div>
                  <div class="rail-card__ticket-actions">
                    <button
                      type="button"
                      class="rail-card__view-conditions"
                      (click)="onViewConditions(ticket, $event)"
                      i18n="@@chatRailViewConditions"
                    >
                      View Conditions
                    </button>
                    <button
                      type="button"
                      class="rail-card__ticket-price"
                      [disabled]="ticket.unavailable"
                      [attr.aria-disabled]="ticket.unavailable ? true : null"
                      (click)="onSelectFare(ticket, $event)"
                      i18n-aria-label="@@chatRailSelectFareAria"
                      attr.aria-label="Select {{ ticket.type }} fare, {{
                        formatCurrency(ticket.price, ticket.currency)
                      }}"
                    >
                      {{ formatCurrency(ticket.price, ticket.currency) }}
                    </button>
                  </div>
                </li>
              }
            </ul>
            <div class="rail-card__tickets-actions">
              <button
                type="button"
                class="rail-card__ok-btn"
                (click)="closeSection($event)"
                i18n="@@chatRailOk"
              >
                Ok
              </button>
            </div>
          </div>
        }
        @if (expandedSection === 'return' && fares.returnTickets?.length) {
          <div class="rail-card__tickets">
            <div class="rail-card__tickets-title" i18n="@@chatRailTicketTypes">Ticket Types</div>
            <ul class="rail-card__ticket-list">
              @for (ticket of fares.returnTickets; track $index) {
                <li class="rail-card__ticket">
                  <div class="rail-card__ticket-info">
                    <span class="rail-card__ticket-type">{{ ticket.type }}</span>
                    @if (ticket.isSplit) {
                      <app-split-ticket-icon
                        class="rail-card__split-icon"
                        [savings]="ticket.savings || ''"
                        [currency]="ticket.currency"
                      ></app-split-ticket-icon>
                    }
                    <app-warning-tags
                      [ticket]="warningTicketFor(ticket)"
                      [displayCheck]="false"
                      (click)="$event.stopPropagation()"
                    ></app-warning-tags>
                  </div>
                  <div class="rail-card__ticket-actions">
                    <button
                      type="button"
                      class="rail-card__view-conditions"
                      (click)="onViewConditions(ticket, $event)"
                      i18n="@@chatRailViewConditions"
                    >
                      View Conditions
                    </button>
                    <button
                      type="button"
                      class="rail-card__ticket-price"
                      [disabled]="ticket.unavailable"
                      [attr.aria-disabled]="ticket.unavailable ? true : null"
                      (click)="onSelectFare(ticket, $event)"
                      i18n-aria-label="@@chatRailSelectFareAria"
                      attr.aria-label="Select {{ ticket.type }} fare, {{
                        formatCurrency(ticket.price, ticket.currency)
                      }}"
                    >
                      {{ formatCurrency(ticket.price, ticket.currency) }}
                    </button>
                  </div>
                </li>
              }
            </ul>

            <div class="rail-card__tickets-actions">
              <button
                type="button"
                class="rail-card__ok-btn"
                (click)="closeSection($event)"
                i18n="@@chatRailOk"
              >
                Ok
              </button>
            </div>
          </div>
        }
      }
    </div>
  }

  <!-- ENT-18234 - the second leg, asked for on the card the fare was picked from -->
  @if (returnPrompt; as prompt) {
    <div
      class="rail-card__return-prompt"
      (click)="$event.stopPropagation()"
      (keydown)="$event.stopPropagation()"
    >
      <p class="rail-card__return-prompt-title">
        @if (prompt.kind === 'trains') {
          <span i18n="@@chatRailPickReturnTrain">Your return train</span>
        } @else {
          <span i18n="@@chatRailPickReturnFare">Please select your return train and fare.</span>
        }
      </p>
      @if (!prompt.journeys.length) {
        <p class="rail-card__return-empty" i18n="@@chatRailNoReturnJourneys">
          No return journeys leave after this one arrives.
        </p>
      }
      @for (option of prompt.journeys; track option.index) {
        <div
          class="rail-card__return-option"
          [class.rail-card__return-option--fares]="prompt.kind === 'fares'"
        >
          <!-- ENT-18234 - a fares question hides each train's list behind its time -->
          @if (prompt.kind === 'fares') {
            <button
              type="button"
              class="rail-card__return-time rail-card__return-time--toggle"
              [class.rail-card__return-time--active]="expandedReturnIndex === option.index"
              [attr.aria-expanded]="expandedReturnIndex === option.index"
              (click)="toggleReturnOption(option.index, $event)"
            >
              <span>
                {{ formatStationTime(option.departDateTime) }} -
                {{ formatStationTime(option.arriveDateTime) }}
              </span>
            </button>
          } @else {
            <span class="rail-card__return-time">
              {{ formatStationTime(option.departDateTime) }} -
              {{ formatStationTime(option.arriveDateTime) }}
            </span>
          }
          @if (prompt.kind === 'trains') {
            <button
              type="button"
              class="rail-card__return-select"
              (click)="onSelectReturnTrain(option, $event)"
              i18n="@@chatRailSelectReturnTrain"
            >
              Select
            </button>
          } @else if (expandedReturnIndex === option.index) {
            <ul class="rail-card__ticket-list">
              @for (ticket of option.tickets; track ticket.fareHash) {
                <li class="rail-card__ticket">
                  <div class="rail-card__ticket-info">
                    <span class="rail-card__ticket-type">{{ ticket.type }}</span>
                    @if (ticket.isSplit) {
                      <app-split-ticket-icon
                        class="rail-card__split-icon"
                        [savings]="ticket.savings || ''"
                        [currency]="ticket.currency"
                      ></app-split-ticket-icon>
                    }
                    <app-warning-tags
                      [ticket]="warningTicketFor(ticket)"
                      [displayCheck]="false"
                      (click)="$event.stopPropagation()"
                    ></app-warning-tags>
                  </div>
                  <div class="rail-card__ticket-actions">
                    <button
                      type="button"
                      class="rail-card__view-conditions"
                      (click)="onViewConditions(ticket, $event)"
                      i18n="@@chatRailViewConditions"
                    >
                      View Conditions
                    </button>
                    <button
                      type="button"
                      class="rail-card__ticket-price"
                      [disabled]="ticket.unavailable"
                      [attr.aria-disabled]="ticket.unavailable ? true : null"
                      (click)="onSelectReturnFare(ticket, $event)"
                      i18n-aria-label="@@chatRailSelectFareAria"
                      attr.aria-label="Select {{ ticket.type }} fare, {{
                        formatCurrency(ticket.price, ticket.currency)
                      }}"
                    >
                      {{ formatCurrency(ticket.price, ticket.currency) }}
                    </button>
                  </div>
                </li>
              }
            </ul>
          }
        </div>
      }
    </div>
  }

  <app-chat-card-feedback
    class="rail-card__feedback"
    [class.rail-card__feedback--eu]="isEuRail"
    [feedbackId]="feedbackId"
    [summary]="feedbackSummary"
    (click)="$event.stopPropagation()"
    (keydown)="$event.stopPropagation()"
  ></app-chat-card-feedback>
</div>
